import type { RouteRecordRaw } from 'vue-router';
import { ANZEIGENAME, KATEGORIE } from '@/persistence/RechteStorage';
import { BuergeRoutes } from '@/buerge/routeHelpers';
import { useBuergeApi } from '@/buerge/api/useBuergeApi';
import { useSharedApi } from '@/shared/api/useSharedApi.ts';
import { Bürgschaftsstatus as Buergschaftsstatus } from '@/types/Bürgschaft.ts';
import { Buergschaftsauftragstatus } from '@/types/Buergschaftsauftrag.ts';

const buergeRoutes: RouteRecordRaw = {
  path: '/buerge',
  component: () => import('./BuergeView.vue'),
  name: 'buerge',
  children: [
    /** AUFGABEN */
    {
      path: 'aufgaben/liste',
      name: BuergeRoutes.BUERGE_AUFGABEN_LISTE,
      component: () => import('./components/aufgaben/AufgabenListe.vue'),
      meta: {
        kategorie: [KATEGORIE.AUFGABEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },

    /** BÜRGSCHAFTSANTRÄGE */

    /** Liste aller Bürgschaftsanträge */
    {
      path: 'buergschaftsauftraege/liste',
      name: 'buergschaftsauftraegeListeBuerge',
      component: () => import('./components/buergschaftsauftraege/BuergschaftsauftragListe.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    /** Bürgschaftsantrag mit den Details anzeigen */
    {
      path: 'buergschaftsauftraege/details/:buergschaftsauftragId(\\d+)',
      name: BuergeRoutes.BUERGE_ANTRAG_DETAIL,
      props: true,
      component: () => import('./components/buergschaftsauftraege/BuergschaftsauftragDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },

    {
      name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_BASE,
      path: 'buergschaftsauftraege/pruefen/:buergschaftsauftragId(\\d+)',
      props: true,
      component: () =>
        import('./components/buergschaftsauftraege/views/AntragPruefen/AntragPruefenBaseView.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.MANUELL_VERWALTEN,
      },
      redirect: { name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS },
      beforeEnter: async (to, from, next) => {
        const buergschaftsauftragId = Number(to.params.buergschaftsauftragId);

        const buergschaftsauftrag =
          await useSharedApi().sharedBuergschaftsauftrag.holeBuergschaftsauftragDetailsFuerAufgabenListe(
            to.params.buergschaftsauftragId as string,
          );

        const isStatusUngeprueft = buergschaftsauftrag.status === Buergschaftsauftragstatus.SCHWEBE;

        if (!isStatusUngeprueft) {
          return next({
            name: BuergeRoutes.BUERGE_ANTRAG_DETAIL,
            params: { buergschaftsauftragId },
          });
        }

        return next();
      },
      children: [
        {
          name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS,
          path: 'details',
          props: true,
          component: () =>
            import(
              './components/buergschaftsauftraege/views/AntragPruefen/AntragPruefenDetails.vue'
            ),
        },

        {
          name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
          path: 'entscheidung',
          props: true,
          component: () =>
            import(
              './components/buergschaftsauftraege/views/AntragPruefen/AntragPruefenEntscheidung.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS,
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ABLEHNEN,
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_AKZEPTIEREN,
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_BEARBEITEN,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS });
          },
        },

        {
          name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_BEARBEITEN,
          path: 'bearbeiten',
          props: true,
          component: () =>
            import(
              './components/buergschaftsauftraege/views/AntragPruefen/AntragPruefenBearbeiten.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ABLEHNEN,
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_AKZEPTIEREN,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS });
          },
        },

        {
          name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_AKZEPTIEREN,
          path: 'akzeptieren',
          props: true,
          component: () =>
            import(
              './components/buergschaftsauftraege/views/AntragPruefen/AntragPruefenAkzeptieren.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
              BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_BEARBEITEN,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS });
          },
        },

        {
          name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ABLEHNEN,
          path: 'ablehnen',
          props: true,
          component: () =>
            import(
              './components/buergschaftsauftraege/views/AntragPruefen/AntragPruefenAblehnen.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_ENTSCHEIDUNG];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_ANTRAG_PRUEFEN_AUFGABE_DETAILS });
          },
        },
      ],
    },

    /** BÜRGSCHAFTEN */
    {
      path: 'buergschaften/liste',
      name: BuergeRoutes.BUERGE_BUERGSCHAFT_LIST,
      component: () => import('./components/buergschaften/BuergschaftenListe.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },

    {
      path: 'buergschaften/erstellen',
      name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN,
      component: () =>
        import('@/buerge/components/buergschaften/views/erstellen/BuergschaftErstellenBase.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.MANUELL_VERWALTEN,
      },
      redirect: { name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_DATEN },
      children: [
        {
          path: 'buergschaftsdaten',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_DATEN,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/erstellen/BuergschaftErstellenBuergschaftsdaten.vue'
            ),
        },
        {
          path: 'text',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_TEXT,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/erstellen/BuergschaftErstellenText.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_DATEN,
              BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_PRUEFEN,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_DATEN });
          },
        },
        {
          path: 'pruefen',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_PRUEFEN,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/erstellen/BuergschaftErstellenPruefen.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_TEXT];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_ERSTELLEN_DATEN });
          },
        },
      ],
    },

    {
      path: 'buergschaften/:buergschaftId(\\d+)/korrigieren',
      name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN,
      props: true,
      component: () =>
        import(
          '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenBase.vue'
        ),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.MANUELL_VERWALTEN,
      },
      redirect: { name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH },
      children: [
        {
          path: 'korrekturwunsch',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenKorrekturwunsch.vue'
            ),
        },
        {
          path: 'daten',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_BUERGSCHAFTSDATEN,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenBuergschaftsdatenBearbeiten.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH,
              BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_PRUEFEN,
            ];

            if (validSteps.includes(from.name.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH });
          },
        },
        {
          path: 'pruefen',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_PRUEFEN,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenPruefen.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_BUERGSCHAFTSDATEN,
            ];

            if (validSteps.includes(from.name.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH });
          },
        },
      ],
    },

    {
      path: 'buergschaften/:buergschaftId(\\d+)/korrigieren',
      name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN,
      props: true,
      component: () =>
        import(
          '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenBase.vue'
        ),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.MANUELL_VERWALTEN,
      },
      redirect: { name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH },
      children: [
        {
          path: 'korrekturwunsch',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenKorrekturwunsch.vue'
            ),
        },
        {
          path: 'text',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_BUERGSCHAFTSDATEN,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenBuergschaftsdatenBearbeiten.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH,
              BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_PRUEFEN,
            ];

            if (validSteps.includes(from.name.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH });
          },
        },
        {
          path: 'pruefen',
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_PRUEFEN,
          component: () =>
            import(
              '@/buerge/components/buergschaften/views/korrigieren/BuergschaftKorrigierenPruefen.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_BUERGSCHAFTSDATEN,
            ];

            if (validSteps.includes(from.name.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_KORRIGIEREN_KORREKTURWUNSCH });
          },
        },
      ],
    },

    /** BÜRGSCHAFT PRÜFEN (ZWEITPRÜFUNG) */
    {
      name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_BASE,
      path: 'buergschaften/:buergschaftId(\\d+)/zweitpruefen',
      props: true,
      component: () =>
        import('./components/buergschaften/views/zweitpruefen/BuergschaftPruefenBaseView.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.MANUELL_VERWALTEN_ZWEITPRUEFUNG,
      },
      redirect: { name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS },
      beforeEnter: async (to, from, next) => {
        const buergschaftId = Number(to.params.buergschaftId);

        const [isLetzterPruefer, buergschaft] = await Promise.all([
          useBuergeApi().pruefungen.istNutzerLetzterPruefer(buergschaftId, 'buergschaft'),
          useSharedApi().sharedBuergschaft.holeBuergschaftDetailsFuerAufgabenListe(
            to.params.buergschaftId as string,
          ),
        ]);

        const isStatusEntwurf = buergschaft.buergschaftsstatus === Buergschaftsstatus.ENT;

        if (isLetzterPruefer || !isStatusEntwurf) {
          return next({
            name: BuergeRoutes.BUERGE_BUERGSCHAFT_DETAIL,
            params: { buergschaftId },
          });
        }

        return next();
      },
      children: [
        {
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS,
          path: 'details',
          props: true,
          component: () =>
            import('./components/buergschaften/views/zweitpruefen/BuergschaftPruefenDetails.vue'),
        },

        {
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
          path: 'entscheidung',
          props: true,
          component: () =>
            import(
              './components/buergschaften/views/zweitpruefen/BuergschaftPruefenEntscheidung.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS,
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_ABLEHNEN,
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_AKZEPTIEREN,
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_AKZEPTIEREN_MIT_AENDERUNG,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS });
          },
        },
        {
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_AKZEPTIEREN,
          path: 'akzeptieren',
          props: true,
          component: () =>
            import(
              './components/buergschaften/views/zweitpruefen/BuergschaftPruefenAkzeptieren.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_AKZEPTIEREN_MIT_AENDERUNG,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS });
          },
        },
        {
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_AKZEPTIEREN_MIT_AENDERUNG,
          path: 'aenderung',
          props: true,
          component: () =>
            import(
              './components/buergschaften/views/zweitpruefen/BuergschaftPruefenAkzeptierenMitAenderung.vue'
            ),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_AKZEPTIEREN,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS });
          },
        },
        {
          name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_ABLEHNEN,
          path: 'ablehnen',
          props: true,
          component: () =>
            import('./components/buergschaften/views/zweitpruefen/BuergschaftPruefenAblehnen.vue'),
          beforeEnter: (to, from, next) => {
            const validSteps: string[] = [
              BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_ENTSCHEIDUNG,
            ];

            if (validSteps.includes(from.name?.toString())) {
              return next();
            }
            return next({ name: BuergeRoutes.BUERGE_BUERGSCHAFT_PRUEFEN_AUFGABE_DETAILS });
          },
        },
      ],
    },

    {
      path: 'buergschaften/details/:buergschaftId(\\d+)/:inanspruchnahmeId(\\d+)?',
      name: 'bürgschaftDetailsBürge',
      props: true,
      component: () => import('./components/buergschaften/BuergschaftDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },

    /** BÜRGSCHAFTSTEXTE */
    {
      path: 'buergschaftstexte/liste',
      name: 'bürgschaftstexteListeBürge',
      component: () => import('./components/buergschaftstexte/BuergschaftstexteListe.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTSTEXTE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'buergschaftstexte/auftraggebertext/details/:buergschaftstemplateId([A-Z][A-Z]\\d{4}-\\d{4}-\\d{3})/:aufgabeId(\\d+)?',
      name: 'auftraggebertextDetailsBürge',
      props: true,
      component: () => import('./components/buergschaftstexte/AuftraggebertextDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTSTEXTE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'buergschaftstexte/auftraggebertext/erstpruefung/:buergschaftstemplateId([A-Z][A-Z]\\d{4}-\\d{4}-\\d{3})/:aktion(\\w+)',
      name: 'auftraggebertextPrüfenErstprüfungBürge',
      props: true,
      component: () =>
        import(
          './components/buergschaftstexte/aktionen/pruefen/AuftraggebertextPruefenErstpruefung.vue'
        ),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTSTEXTE],
        anzeigeName: ANZEIGENAME.ERSTPRÜFEN,
      },
    },
    {
      path: 'buergschaftstexte/auftraggebertext/zweitpruefung/:buergschaftstemplateId([A-Z][A-Z]\\d{4}-\\d{4}-\\d{3})/:aufgabeId(\\d+)?/:aktion(\\w+)',
      name: 'auftraggebertextPrüfenZweitprüfungBürge',
      props: true,
      component: () =>
        import(
          './components/buergschaftstexte/aktionen/pruefen/AuftraggebertextPruefenZweitpruefung.vue'
        ),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTSTEXTE],
        anzeigeName: ANZEIGENAME.ZWEITPRÜFEN,
      },
    },
    {
      path: 'buergschaftstexte/buergentext/details/:buergschaftstemplateId([A-Z][A-Z]\\d{4}-\\d{4}-\\d{3})',
      name: 'bürgentextDetailsBürge',
      props: true,
      component: () => import('./components/buergschaftstexte/BuergentextDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTSTEXTE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'buergschaftstexte/oeffentlichertext/details/:buergschaftstemplateId([A-Z][A-Z]\\d{4}-\\d{4}-\\d{3})',
      name: 'öffentlicherTextDetailsBürge',
      props: true,
      component: () => import('./components/buergschaftstexte/OeffentlicherTextDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.BÜRGSCHAFTSTEXTE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },

    /** UNTERNEHMEN */
    {
      path: 'unternehmen/liste',
      name: 'unternehmenListeBuerge',
      component: () => import('./components/unternehmen/UnternehmenListe.vue'),
      meta: {
        kategorie: [KATEGORIE.UNTERNEHMEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'unternehmen/:unternehmenId/uebersicht',
      name: 'unternehmenUebersichtBuerge',
      props: true,
      component: () => import('./components/unternehmen/UnternehmenUebersicht.vue'),
      meta: {
        kategorie: [KATEGORIE.UNTERNEHMEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    /** VERTRÄGE */
    {
      path: 'vertraege/liste',
      name: 'verträgeListeBürge',
      component: () => import('./components/vertraege/VertraegeListe.vue'),
      meta: {
        kategorie: [KATEGORIE.VERTRÄGE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'vertraege/details/:kautionsvertragId/:vertragsnummer',
      name: 'vertragDetailsBürge',
      props: true,
      component: () => import('./components/vertraege/VertragDetails.vue'),
      meta: {
        kategorie: [KATEGORIE.VERTRÄGE],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'vertraege/:kautionsvertragId/:pruefungId(P-\\d{6})/zuordnung-pruefen',
      name: 'vertragZuordnungPrüfenBürge',
      props: true,
      component: () => import('./components/vertraege/VertragZuordnungPruefen.vue'),
      meta: {
        kategorie: [KATEGORIE.VERTRÄGE],
        anzeigeName: ANZEIGENAME.PRÜFEN,
      },
    },
    {
      path: 'vertraege/:kautionsvertragId/:pruefungId(P-\\d{6})/zuordnung-pruefen-abschliessen',
      name: 'vertragZuordnungPrüfenAbschließenBürge',
      props: true,
      component: () => import('./components/vertraege/VertragZuordnungPruefenAbschliessen.vue'),
      meta: {
        kategorie: [KATEGORIE.VERTRÄGE],
        anzeigeName: ANZEIGENAME.PRÜFEN,
      },
    },
    {
      path: 'vertraege/:kautionsvertragId/:pruefungId(P-\\d{6})/zuordnung-pruefen-erfolg',
      name: 'vertragZuordnungPrüfenErfolgBürge',
      props: true,
      component: () => import('./components/vertraege/common/VertragZuordnungPruefenErfolg.vue'),
      meta: {
        kategorie: [KATEGORIE.VERTRÄGE],
        anzeigeName: ANZEIGENAME.PRÜFEN,
      },
    },

    /** NUTZER UND ROLLEN */
    {
      path: 'nutzer/liste/:aktiverTab?',
      name: 'nutzerRollenListeBürge',
      component: () => import('./components/nutzer/NutzerRollenListe.vue'),
      meta: {
        kategorie: [KATEGORIE.NUTZER, KATEGORIE.ROLLEN],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'nutzer/anlegen',
      name: 'nutzerErstellenBürge',
      component: () => import('./components/nutzer/NutzerErstellen.vue'),
      meta: {
        kategorie: [KATEGORIE.NUTZER],
        anzeigeName: ANZEIGENAME.VERWALTEN,
      },
    },
    {
      path: 'nutzer/wiederherstellen',
      name: 'nutzerWiederherstellenBürge',
      component: () => import('./components/nutzer/NutzerWiederherstellen.vue'),
      meta: {
        kategorie: [KATEGORIE.NUTZER],
        anzeigeName: ANZEIGENAME.VERWALTEN,
      },
    },
    {
      path: 'nutzer/rollen/anlegen',
      name: 'rolleErstellenBürge',
      component: () => import('./components/nutzer/RolleErstellen.vue'),
      meta: {
        kategorie: [KATEGORIE.ROLLEN],
        anzeigeName: ANZEIGENAME.VERWALTEN,
      },
    },
    {
      path: 'nutzer/:nutzerId([0-9a-fA-F]{8}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{4}\\b-[0-9a-fA-F]{12})/bearbeiten',
      name: 'nutzerBearbeitenBürge',
      props: true,
      component: () => import('./components/nutzer/NutzerBearbeiten.vue'),
      meta: {
        kategorie: [KATEGORIE.NUTZER],
        anzeigeName: ANZEIGENAME.ANZEIGEN,
      },
    },
    {
      path: 'nutzer/rollen/bearbeiten',
      name: 'rolleBearbeitenBürge',
      component: () => import('./components/nutzer/RolleBearbeiten.vue'),
      meta: {
        kategorie: [KATEGORIE.ROLLEN],
        anzeigeName: ANZEIGENAME.VERWALTEN,
      },
    },

    /** KONTOÜBERSICHT */
    {
      path: 'kontouebersicht',
      name: 'kontoÜbersichtBürge',
      props: {
        domaene: 'avalgeber',
      },
      component: () => import('../shared/Nutzer/Components/KontoUebersicht.vue'),
    },

    /** ERROR SCREEN FEHLENDE RECHTE */
    {
      path: 'error',
      name: 'fehlerScreenFehlendeRechteBürge',
      component: () => import('./components/common/FehlerScreenFehlendeRechte.vue'),
    },
  ],
};

export { buergeRoutes };
